// Minimal error-toast utility built on MUI's Snackbar. Mounted once at
// the top of the app; pages anywhere call `useErrorToast()` to surface
// a transient error message. Mostly used for non-blocking failures
// (perk usage logging, receipt upload, etc.) that previously went to
// console.error.
//
// For blocking errors (save failed, navigation halted), keep using
// inline <Alert> on the page so the user can't miss it.
//
// Hook + context live in ./useErrorToast so this file can stay
// component-only (Fast Refresh requirement).

import { useCallback, useState, type ReactNode } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { toUserMessage } from '../lib/supabaseErrors';
import { ErrorToastContext } from './useErrorToast';

interface ToastState {
  open: boolean;
  message: string;
  severity: 'error' | 'warning' | 'info' | 'success';
}

export function ErrorToastProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ToastState>({ open: false, message: '', severity: 'error' });

  const showError = useCallback((err: unknown, fallback?: string) => {
    setState({ open: true, message: toUserMessage(err, fallback), severity: 'error' });
  }, []);

  const showMessage = useCallback((message: string, severity: 'info' | 'success' | 'warning' = 'info') => {
    setState({ open: true, message, severity });
  }, []);

  return (
    <ErrorToastContext.Provider value={{ showError, showMessage }}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClose={() => setState((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={state.severity}
          onClose={() => setState((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {state.message}
        </Alert>
      </Snackbar>
    </ErrorToastContext.Provider>
  );
}
